import { styled } from '@mui/material/styles';
import { Box, Typography, Checkbox, Switch } from '@mui/material';

// 일괄중지 모달 전용 스타일. 헤더/푸터 등 공통 요소는
// ChangeTFreezyModalStyled(ModalHeader, ModalTitle, CloseButton, ModalContent, FormSection,
// ButtonWrapper, CancelButton, SaveButton, StyledSelect)를 그대로 재사용한다. 여기서는
// 상단 입력 폼/필터/카드 그리드/요약처럼 이 모달에만 존재하는 부분만 정의한다.

// 다른 모달보다 리스트가 들어가는 만큼 폭을 넓게 잡는다.
export const ModalContainer = styled(Box)({
  width: '880px',
  maxHeight: '86vh',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

// ============================================
// 상단 입력 폼 (기준 날짜 / 중지일 / 관리 담당자 / 메모)
// ============================================
export const TopFormGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
});

export const TopFormLeftCol = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// 기준 날짜 / 종료 날짜를 한 줄에 나란히 배치. 두 필드가 폭을 균등 분할해 날짜가 안 가리게 한다.
export const DateRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  '& > *': {
    flex: 1,
    minWidth: 0,
  },
});

export const TopFormField = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const TopFieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

// 중지 종료 날짜 아래 "N일간 중지" 안내 — 선택 범위의 일수를 즉시 피드백.
export const DurationHint = styled(Typography)({
  marginTop: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#0F7F85',
});

// ============================================
// 필터 탭
// ============================================
export const FilterTabsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const FilterChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '6px 14px',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
  border: `1px solid ${isActive ? '#0F7F85' : '#d9d9d9'}`,
  background: isActive ? '#0F7F85' : '#fff',
  color: isActive ? '#fff' : '#595959',
}));

// ============================================
// 전체선택 + 중지 제한 해제 토글
// ============================================
export const SelectAllRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '4px',
  padding: '4px 0',
});

export const SelectAllLeftGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const SelectAllLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  cursor: 'pointer',
});

export const ToggleGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ToggleHintText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#a1a1a1',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

export const TealSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0F7F85',
  },
});

// ============================================
// 상품 카드 그리드
// ============================================
export const CardsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  maxHeight: '320px',
  overflowY: 'auto',
  padding: '4px 2px',
});

export const ListEmptyState = styled(Box)({
  gridColumn: '1 / -1',
  padding: '32px 16px',
  textAlign: 'center',
  color: '#a1a1a1',
  fontSize: '14px',
});

export const ProductCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '12px',
  border: '1px solid #ebebeb',
  borderRadius: '8px',
  background: disabled ? '#fafafa' : '#fff',
  opacity: disabled ? 0.55 : 1,
  cursor: disabled ? 'default' : 'pointer',
  minWidth: 0,
}));

export const ItemCheckbox = styled(Checkbox)({
  padding: '4px',
  marginTop: '2px',
  color: '#c0c0c0',
  '&.Mui-checked': {
    color: '#0F7F85',
  },
});

export const ItemInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const ItemNameRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});

export const ItemTypeLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#0F7F85',
  background: '#E0F4F5',
  borderRadius: '4px',
  padding: '2px 6px',
  flexShrink: 0,
});

export const ItemName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

export const ItemMetaRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '4px 10px',
});

export const ItemMetaText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
});

export const ItemStopInfoText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#a1a1a1',
});

// ============================================
// 요약 바
// ============================================
export const SummaryBar = styled(Box)({
  background: '#F7F9FA',
  border: '1px solid #EEF0F2',
  borderRadius: '8px',
  padding: '12px 16px',
  textAlign: 'center',
});

export const SummaryText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#404040',
});

export const SummaryHighlight = styled('span')({
  fontWeight: 700,
  color: '#0F7F85',
});
